import type { Dispatch, MouseEvent, RefObject, SetStateAction } from 'react'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'

import type {
  ChartTypePickerSection,
  ChartTypePickerSectionSpec,
} from 'features/charts-v2/components/ChartTypePicker/ChartTypePicker'
import type {
  ChartPreset,
  ChartPresetGroup,
  ChartTypeV2,
} from 'features/charts-v2/types/chart-types'
import { CHART_PRESET_GROUPS } from 'features/charts-v2/types/ChartPresets'

// Minimum gap kept between the child menu's bottom edge and the viewport
// bottom when clamping its offset.
const CHILD_MENU_VIEWPORT_GUTTER = 20

// Mirrors ChildMenu's own `margin-left: 8px` / `margin-right: 8px` in
// chart-type-picker.styles.ts — kept as one named constant so a future
// change to one is easy to spot needing the other.
const CHILD_MENU_HORIZONTAL_GAP = 8

export interface UseChartTypePickerParams {
  sections: readonly ChartTypePickerSectionSpec[]
  selectedChartType?: ChartTypeV2
  selectedPresetId?: string | null
  onChartTypeChange: (chartType: ChartTypeV2) => void
  onPresetSelect?: (preset: ChartPreset | null) => void
}

export interface UseChartTypePickerResult {
  showDropdown: boolean
  setShowDropdown: Dispatch<SetStateAction<boolean>>
  activeGroup: string | null
  setActiveGroup: Dispatch<SetStateAction<string | null>>
  childMenuOffset: number
  shouldFlipChildMenu: boolean
  parentMenuRef: RefObject<HTMLDivElement>
  childMenuRef: RefObject<HTMLDivElement>
  currentPreset: ChartPreset | null
  currentPresetGroup: ChartPresetGroup | null
  visibleGroups: ChartPresetGroup[]
  activeGroupPresets: ChartPreset[] | null
  activeCustomItems: ChartTypePickerSection['items'] | null
  handleGroupHover: (
    groupKey: string,
    event: MouseEvent<HTMLDivElement>,
  ) => void
  handlePresetClick: (preset: ChartPreset) => void
  handleCustomItemClick: (id: string) => void
}

const isCustomSection = (
  section: ChartTypePickerSectionSpec,
): section is ChartTypePickerSection => typeof section !== 'string'

/**
 * All the picker's state, derived selection lookups, and event handlers —
 * split out of `ChartTypePicker` so the component itself stays pure JSX
 * composition (the `component-file-too-large` gate on the combined shape).
 */
export function useChartTypePicker({
  sections,
  selectedChartType,
  selectedPresetId,
  onChartTypeChange,
  onPresetSelect,
}: UseChartTypePickerParams): UseChartTypePickerResult {
  const [showDropdown, setShowDropdown] = useState(false)
  const [activeGroup, setActiveGroup] = useState<string | null>(null)
  // The raw hover-measured offset, and the viewport-clamped value actually
  // rendered — kept separate so the clamp effect below can read the raw
  // value without depending on (and re-triggering off) its own output.
  const [rawOffsetTop, setRawOffsetTop] = useState(0)
  const [childMenuOffset, setChildMenuOffset] = useState(0)
  const [shouldFlipChildMenu, setShouldFlipChildMenu] = useState(false)
  const parentMenuRef = useRef<HTMLDivElement>(null)
  const childMenuRef = useRef<HTMLDivElement>(null)

  // Helper to find freeform preset for a given chart type
  const findFreeformPreset = (chartType: ChartTypeV2): ChartPreset | null => {
    const freeformGroup = CHART_PRESET_GROUPS.find((g) => g.key === 'freeform')
    if (!freeformGroup) return null
    return freeformGroup.presets.find((p) => p.chartType === chartType) ?? null
  }

  // Look up the selected preset if we have a preset ID, otherwise fall back
  // to the freeform preset for the given chart type — a tile with no chart
  // type (a dashboard widget/text tile) resolves to no preset at all.
  const currentPreset = useMemo(() => {
    if (selectedPresetId) {
      for (const group of CHART_PRESET_GROUPS) {
        const preset = group.presets.find((p) => p.id === selectedPresetId)
        if (preset) return preset
      }
    }
    return selectedChartType ? findFreeformPreset(selectedChartType) : null
  }, [selectedPresetId, selectedChartType])

  // Look up the parent group for the current preset
  const currentPresetGroup = useMemo(() => {
    if (selectedPresetId) {
      for (const group of CHART_PRESET_GROUPS) {
        const preset = group.presets.find((p) => p.id === selectedPresetId)
        if (preset) return group
      }
    }
    if (currentPreset) {
      return CHART_PRESET_GROUPS.find((g) => g.key === 'freeform') ?? null
    }
    return null
  }, [selectedPresetId, currentPreset])

  // Memoize filtered groups (static data, no dependencies)
  const visibleGroups = useMemo(
    () => CHART_PRESET_GROUPS.filter((g) => g.key !== 'freeform'),
    [],
  )

  // Memoize active group presets (depends on activeGroup state)
  const activeGroupPresets = useMemo(() => {
    const group = CHART_PRESET_GROUPS.find((g) => g.key === activeGroup)
    return group?.presets ?? null
  }, [activeGroup])

  const activeCustomSection = useMemo(
    () =>
      sections.find(
        (section) => isCustomSection(section) && section.key === activeGroup,
      ) as ChartTypePickerSection | undefined,
    [sections, activeGroup],
  )
  const activeCustomItems = activeCustomSection?.items ?? null

  const handleGroupHover = (
    groupKey: string,
    event: MouseEvent<HTMLDivElement>,
  ): void => {
    setActiveGroup(groupKey)
    if (parentMenuRef.current) {
      const parentMenuRect = parentMenuRef.current.getBoundingClientRect()
      const itemRect = event.currentTarget.getBoundingClientRect()
      setRawOffsetTop(itemRect.top - parentMenuRect.top)
    }
  }

  const handlePresetClick = (preset: ChartPreset): void => {
    if (onPresetSelect) {
      onPresetSelect(preset)
    } else {
      onChartTypeChange(preset.chartType)
    }
    setShowDropdown(false)
    setActiveGroup(null)
  }

  const handleCustomItemClick = (id: string): void => {
    activeCustomSection?.onSelect(id)
    setShowDropdown(false)
    setActiveGroup(null)
  }

  // Clamp the raw hover offset so the child menu never overflows the
  // viewport bottom. Reads `rawOffsetTop` (set on hover) and writes the
  // separate `childMenuOffset` that's actually rendered — never the other
  // way around — so switching back to a shorter group restores the raw
  // offset instead of ratcheting down forever.
  //
  // Also decides whether the child menu should flip to the LEFT of the
  // parent menu. ChildMenu is `position: absolute`, so it contributes
  // nothing to the popup's own bounding rect — the popover's built-in
  // viewport-collision flip only ever sees the parent menu and has no idea
  // this flyout exists, so it never gets flipped by the popover. We measure
  // it ourselves the same way childHeight is already measured here, rather
  // than hardcoding its declared width.
  useLayoutEffect(() => {
    if (!activeGroup || !childMenuRef.current || !parentMenuRef.current) {
      return
    }
    const childHeight = childMenuRef.current.offsetHeight
    const childWidth = childMenuRef.current.offsetWidth
    const parentMenuRect = parentMenuRef.current.getBoundingClientRect()
    const maxOffset =
      window.innerHeight -
      CHILD_MENU_VIEWPORT_GUTTER -
      parentMenuRect.top -
      childHeight
    setChildMenuOffset(Math.max(0, Math.min(rawOffsetTop, maxOffset)))
    setShouldFlipChildMenu(
      parentMenuRect.right + CHILD_MENU_HORIZONTAL_GAP + childWidth >
        window.innerWidth,
    )
  }, [activeGroup, rawOffsetTop])

  return {
    showDropdown,
    setShowDropdown,
    activeGroup,
    setActiveGroup,
    childMenuOffset,
    shouldFlipChildMenu,
    parentMenuRef,
    childMenuRef,
    currentPreset,
    currentPresetGroup,
    visibleGroups,
    activeGroupPresets,
    activeCustomItems,
    handleGroupHover,
    handlePresetClick,
    handleCustomItemClick,
  }
}
